import {
  Button,
  Grid,
  LinearProgress,
  Menu,
  MenuItem,
  styled,
  Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { FaComment } from 'react-icons/fa';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
// adding App Insights hook import for telemetry logging
import { useAppInsights } from 'src/hooks/useAppInsights';
import { IFilterObj } from 'src/components/filters';
import { transformDynamicFormValueToFilterPayload } from 'src/components/filters/utils/filter-form-utils';
import FilterLoadingSkeleton from 'src/components/skeletons/filtersLoadingSkeleton';
import {
  useAppDispatch,
  useAppSelector,
  useDialogVisibilityHook,
  useMenu
} from 'src/hooks';
import useMatomoFunction from 'src/hooks/use-matomo-function.hook';
import useModuleMetadata from 'src/hooks/use-module-metadata.hook';
import { FormField2, IDynamicFormValue } from 'src/lib';
import Storage from 'src/utils/storage/storage';
import _ from 'lodash';
import { LocalFilters } from '../common';
import { GridTableComponentV2, ModuleSaveButton } from '../common/components';
import Legends from '../common/components/legends/legends';
import PHLegends from '../common/components/legends/ph-legends';
import {
  fetchFilterFields,
  FiltersWrapper,
  setGlobalFilterValues
} from '../common/features';
import useFilterFetch from '../common/hooks/use-filter-fetch.hook';
import useModulePreRequisites from '../common/hooks/use-module-pre-requisites.hook';
import SaveAsContainer from '../components/save-as-container/save-as-container';
import SimulatorFooter from '../components/simulation-footer/simulator-footer';
import {
  MatomoEventTrackMapObject,
  NON_FINANCIAL_SEGMENTS
} from '../constants/constant';
import ScenarioCreateSuccessDialog from '../home/components/scenario-create-success/scenario-create-success-dialog';
import MODULE_TO_PAGE_MAP from '../home/types/simulation-module-to-page-map';
import AddSKUDialog from './components/add-sku/add-sku-dialog';
import CogsDialog from './components/cogs-dialog/cogs-dialog';
import DownloadSummaryButton from './components/download-summary/download-summary';
import DynamicDrawerBase from './components/drawer/dynamic-drawer';
import OpportunitySummaryTable from './components/opportunity-summary/opportunity-summary-table';
import useOpportunitySummary from './components/opportunity-summary/use-opportunity-summary-hook';
import PlannerDynamicToggle from './components/planner-toggle/planner-toggle';
import { getListOfSavedScenarios } from '../scenarios/store/scenario-slice';
import {
  resetCogsFilters,
  resetGridMetrics,
  setCogsDialogVisible,
  setLocalFilterValue,
  toggleDrawerView,
  toggleViewPhRecommendation
} from './store/planner-slice';
import {
  getGridData,
  getLocalFiltersData,
  getPHRecommendations,
  getPlannerToggles,
  getProductsWithComments,
  savePlanner
} from './store/planner-slice-async-thunks';
import DRAWER_VIEW_TYPE from './types/drawer-view-type';
import { IPlannerToggle } from './types/planner-toggle.model';
import getLocalFiltersPayload from './utils/utils';
import OnNextClickHandler from '../common/utils/simulator-page-next-button';



const StyledGrid = styled(Grid)`
  height: 100%;
  width: 100%;
  overflow-y: auto;
`;

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  padding: theme.spacing(0.6, 1),
  fontSize: '12px'
}));

export default function Planner() {
  const appDispatch = useAppDispatch();

  // adding App Insights hook initialization for telemetry logging
  const { trackEvent } = useAppInsights();

  const [searchParams] = useSearchParams();
  const { callFunction } = useMatomoFunction();
  const { source } = useAppSelector((state) => state.user);

  // moving moduleId, sourceId, scenarioId to top to avoid "used before defined" errors
  const { moduleId, sourceId, scenarioId } = useModuleMetadata({
    module: MODULE_TO_PAGE_MAP.PLANNER
  });

  // moving currentScenarioId and navigate to top to avoid "used before defined" errors
  const { id: currentScenarioId } = useParams();
  const navigate = useNavigate();

  const {
    gridLayout,
    fetchingGridData,
    toggledView,
    isSummaryRefreshRequired,
    localFilterFormFields,
    localFilterInitialValues,
    localFilterValue,
    fetchingLocalFiltersData,
    drawerViewMetadata,
    toggles,
    appliedFilters,
    isFetchingOpportunitySummaryData,
    isFetchingPhRecommendations
  } = useAppSelector((state) => state.plannerV2);

  const segmentDetailsStorage = Storage.getSegmentDetails();
  const { segmentDetailsData } = useAppSelector((state) => state.landing);

  const segmentDetailsDataObject = !_.isEmpty(segmentDetailsData)
    ? segmentDetailsData
    : segmentDetailsStorage !== null
      ? segmentDetailsStorage
      : {};

  const gridStaticContent = toggles.find((toggle) => toggle.key === toggledView)
    ? toggles.find((toggle) => toggle.key === toggledView).alias
    : 'PPG';

  const {
    getAndSetOpportunitySummaryTableRows,
    columns: opportunitySummaryTableColumns,
    rows: opportunitySummaryTableRows
  } = useOpportunitySummary();

  const {
    globalFilterCombinations,
    globalFilterFormFields,
    globalFilterValue
  } = useAppSelector((state) => state.filters);

  const { dynamicBreadcrumbs } = useAppSelector((state) => state.simulatorHome);
  const { planner: plannerAccessConfig } = useAppSelector(
    (state) => state.simulatorHome.moduleAccessMap
  );

  // getting state of current scenario details
  const { moduleAccessMap } = useAppSelector((state) => state.simulatorHome);
  // setting kpi button enabled/disabled based on access_config key in currentScenario object
  const isKpiEnabled = !!moduleAccessMap?.kpi_update;

  const { isDrawerOpen, viewType } = useAppSelector(
    (state) => state.plannerV2.drawerViewMetadata
  );

  const [isLocalFiltersReset, setIsLocalFiltersReset] = useState(false);

  const [isScenarioSuccessDialogOpen, setIsScenarioSuccessDialogOpen] =
    useState(false);

  const { isDialogOpen: isBulkInputsDialogOpen, handleDialogVisibilityToggle } =
    useDialogVisibilityHook();

  const {
    isDialogOpen: isAddSKUDialogOpen,
    handleDialogVisibilityToggle: handleAddSKUDialogVisibility
  } = useDialogVisibilityHook();

  // getting scenario list from state
  const { listOfScenarios } = useAppSelector((state) => state.scenarios);

  const handleScenarioCreateSuccessDialogClose = () => {
    setIsScenarioSuccessDialogOpen(false);
  };

  // localstate for cogs dialog open/close
  const [isCogsDialogOpen, setIsCogsDialogOpen] = useState(false);

  const getPhFollowedStatus = () =>
    listOfScenarios.filter(
      (scenarioItem) => scenarioItem.pk_id === scenarioId
    )[0]?.is_ph_followed;

  // local state for follow ph
  const [isFollowPH, setFollowPH] = useState(getPhFollowedStatus() || false);

  // local state for view ph recommendations
  const [isViewPHRecommendations, setViewPHRecommendations] = useState(false);

  // local state for menu item
  const { isMenuOpen, anchorEl, handleMenuOpen, handleMenuClose } = useMenu();

  useEffect(() => {
    appDispatch(getListOfSavedScenarios({ source_id: source }))
  }, [isFollowPH]);

  // handler for cogs dialog open
  const handleDialogOpen = () => {
    // adding telemetry log for COGS dialog open
    trackEvent('Planner_CogsDialogOpened', {
      scenarioId: String(scenarioId || 'new'),
      moduleId: String(moduleId || ''),
      sourceId: String(sourceId || ''),
      segmentName: segmentDetailsDataObject?.source_name?.toLowerCase().replace(/ /g, '')
    });
    setIsCogsDialogOpen(true);
    appDispatch(setCogsDialogVisible());
  };

  // handler for cogs dialog close
  const handleDialogClose = () => {
    // adding telemetry log for COGS dialog close
    trackEvent('Planner_CogsDialogClosed', {
      scenarioId: String(scenarioId || 'new'),
      moduleId: String(moduleId || ''),
      sourceId: String(sourceId || '')
    });
    setIsCogsDialogOpen(false);
    appDispatch(resetCogsFilters());
    appDispatch(setCogsDialogVisible());
  };

  // the handler function for the next button functionality
  const handleNextFn = () => {
    OnNextClickHandler(
      dynamicBreadcrumbs,
      MODULE_TO_PAGE_MAP.PLANNER,
      currentScenarioId,
      navigate
    );
  };

  const getThunkPayload = () => ({
    moduleId,
    sourceId,
    scenarioId
  });

  const getGridDataAndOpportunitySummaryTableData = (
    filters: IFilterObj[],
    localFilterFormValue: IDynamicFormValue,
    localFilterFields: FormField2[],
    toggle: string
  ) => {
    const localFilters = getLocalFiltersPayload(
      localFilterFormValue,
      localFilterFields
    );
    const payload = {
      ...getThunkPayload(),
      filters,
      localFilters
    };
    return appDispatch(getGridData(payload)).then(() => {
      getAndSetOpportunitySummaryTableRows({
        filters,
        localFilters,
        moduleId,
        scenarioId,
        sourceId,
        toggle
      });
    });
  };

  const [currentGlobalFilterValue, setCurrentGlobalFilterValue] =
    useState<IDynamicFormValue>({});

  const { isFetchingFilterFields } = useFilterFetch({
    sourceId,
    scenarioId,
    moduleId,
    filterFieldFetchThunk: (arg) =>
      appDispatch(
        fetchFilterFields({
          sourceId: arg.sourceId,
          moduleId: arg.moduleId,
          scenarioId: arg.scenarioId
        })
      )
  });

  const { isFetchingPreRequisites } = useModulePreRequisites({
    module: MODULE_TO_PAGE_MAP.PLANNER
  });

  const onGlobalFilterFieldsFetched = (
    filters: IFilterObj[],
    formValue: IDynamicFormValue
  ) => {
    setCurrentGlobalFilterValue(formValue);
    appDispatch(
      getLocalFiltersData({
        sourceID: sourceId,
        scenarioId: Number(scenarioId),
        filters
      })
    ).then((response) => {
      const { values, fields } = response?.payload as {
        values: IDynamicFormValue;
        fields: FormField2[];
      };
      return appDispatch(getPlannerToggles({ sourceId })).then(
        (plannerToggleResponse) => {
          const plannerToggles =
            plannerToggleResponse.payload as IPlannerToggle[];
          const defaultToggle = plannerToggles[0].key;
          return getGridDataAndOpportunitySummaryTableData(
            filters,
            values,
            fields,
            defaultToggle
          );
        }
      );
    });
  };

  const getPageName = () => {
    const pageTitle = window.location.href.split('/').at(-1);
    return pageTitle;
  };

  // adding telemetry log for page load with business context
  useEffect(() => {
    callFunction(
      MatomoEventTrackMapObject.track_page_view.event_key,
      getPageName()
    );

    trackEvent('Planner_PageLoaded', {
      scenarioId: String(scenarioId || 'new'),
      moduleId: String(moduleId || ''),
      sourceId: String(sourceId || ''),
      segmentName: segmentDetailsDataObject?.source_name?.toLowerCase().replace(/ /g, ''),
      defaultToggle: toggles[0]?.key,
      accessLevel: plannerAccessConfig?.save ? 'full' : 'read-only'
    });
  }, []);

  useEffect(() => {
    if (searchParams.get('showSuccess')) {
      setIsScenarioSuccessDialogOpen(true);
    }
  }, []);

  useEffect(() => {
    appDispatch(toggleViewPhRecommendation(false));
  }, []);

  const onOpportunityChange = (refreshSummary: boolean): void => {
    if (refreshSummary || isSummaryRefreshRequired) {
      const filters = transformDynamicFormValueToFilterPayload({
        combinations: globalFilterCombinations,
        formFields: globalFilterFormFields,
        formValue: globalFilterValue
      });
      getGridDataAndOpportunitySummaryTableData(
        filters,
        localFilterValue,
        localFilterFormFields,
        toggledView
      );
    }
  };

  const handlePlannerSave = async () => {
    // adding telemetry log for scenario save action
    trackEvent('Planner_ScenarioSaved', {
      scenarioId: String(scenarioId || 'new'),
      moduleId: String(moduleId || ''),
      sourceId: String(sourceId || ''),
      toggledView,
      isFollowPH,
      isViewPHRecommendations
    });
    appDispatch(savePlanner(scenarioId));
  };

  const handleGlobalFilterApply = (
    filters: IFilterObj[],
    isReset: boolean,
    formValue: IDynamicFormValue
  ) => {
    // adding telemetry log for global filter apply action
    trackEvent('Planner_GlobalFiltersApplied', {
      filterCount: filters?.length || 0,
      isReset,
      scenarioId: String(scenarioId || 'new'),
      moduleId: String(moduleId || ''),
      sourceId: String(sourceId || ''),
      toggledView
    });

    if (isReset) {
      appDispatch(resetGridMetrics());
      setIsLocalFiltersReset(true);
      setCurrentGlobalFilterValue(formValue);
      appDispatch(
        getLocalFiltersData({
          sourceID: sourceId,
          scenarioId: Number(scenarioId),
          filters
        })
      ).then((response) => {
        const { values } = response.payload as {
          values: IDynamicFormValue;
          fields: FormField2[];
        };
        return getGridDataAndOpportunitySummaryTableData(
          filters,
          values,
          localFilterFormFields,
          toggledView
        ).then(() => setIsLocalFiltersReset(false));
      });
    }
    setIsLocalFiltersReset(false);
    appDispatch(
      getLocalFiltersData({
        sourceID: sourceId,
        scenarioId: Number(scenarioId),
        filters
      })
    ).then((response) => {
      const { values } = response.payload as {
        values: IDynamicFormValue;
        fields: FormField2[];
      };

      return getGridDataAndOpportunitySummaryTableData(
        filters,
        values,
        localFilterFormFields,
        toggledView
      );
    });
  };

  const handleLocalFilterApply = (filterValue: IDynamicFormValue) => {
    // adding telemetry log for local filter apply action
    trackEvent('Planner_LocalFiltersApplied', {
      filterKeys: Object.keys(filterValue || {}).join(','),
      scenarioId: String(scenarioId || 'new'),
      moduleId: String(moduleId || ''),
      sourceId: String(sourceId || ''),
      toggledView
    });

    appDispatch(setGlobalFilterValues(currentGlobalFilterValue));
    const filters = transformDynamicFormValueToFilterPayload({
      combinations: globalFilterCombinations,
      formFields: globalFilterFormFields,
      formValue: currentGlobalFilterValue
    });
    return getGridDataAndOpportunitySummaryTableData(
      filters,
      filterValue,
      localFilterFormFields,
      toggledView
    );
  };

  const performActionOnChange = (value) => {
    setCurrentGlobalFilterValue(value);
  };

  // handler for toggling follow ph button
  const handleFollowPH = () => {
    // adding telemetry log for PH follow toggle action
    trackEvent('Planner_PHFollowToggled', {
      previousValue: isFollowPH,
      newValue: !isFollowPH,
      scenarioId: String(scenarioId || 'new'),
      moduleId: String(moduleId || ''),
      sourceId: String(sourceId || ''),
      triggeredFrom: 'menu'
    });

    handleMenuClose();
    return appDispatch(
      getPHRecommendations({ ...getThunkPayload(), phFollow: !isFollowPH })
    ).then((res) => {
      setFollowPH(!isFollowPH);
      Storage.setPhRecommendationStatus(!isFollowPH);

      const filters = transformDynamicFormValueToFilterPayload({
        combinations: globalFilterCombinations,
        formFields: globalFilterFormFields,
        formValue: currentGlobalFilterValue
      });
      return getGridDataAndOpportunitySummaryTableData(
        filters,
        localFilterValue,
        localFilterFormFields,
        toggledView
      );
    });
  };

  if (isFetchingPreRequisites) {
    return (
      <Grid container>
        <Grid item xs={12}>
          <LinearProgress />
        </Grid>
      </Grid>
    );
  }

  // handler for viewing/hiding ph recommendation
  const handleViewPhRecommendation = () => {
    // adding telemetry log for PH view recommendations toggle
    trackEvent('Planner_PHViewRecommendationsToggled', {
      previousValue: isViewPHRecommendations,
      newValue: !isViewPHRecommendations,
      scenarioId: String(scenarioId || 'new'),
      moduleId: String(moduleId || ''),
      sourceId: String(sourceId || ''),
      triggeredFrom: 'menu'
    });

    handleMenuClose();
    setViewPHRecommendations(!isViewPHRecommendations);
    appDispatch(toggleViewPhRecommendation(!isViewPHRecommendations));
  };

  // hanlder for showing ph tool info
  const handlePHInfo = () => {
    // adding telemetry log for PH info dialog open
    trackEvent('Planner_PHInfoOpened', {
      scenarioId: String(scenarioId || 'new'),
      moduleId: String(moduleId || ''),
      sourceId: String(sourceId || '')
    });
    handleMenuClose();
  };

  // adding telemetry log for Add SKU dialog open
  const handleAddSKUOpen = () => {
    trackEvent('Planner_AddSKUDialogOpened', {
      scenarioId: String(scenarioId || 'new'),
      moduleId: String(moduleId || ''),
      sourceId: String(sourceId || '')
    });
    handleAddSKUDialogVisibility();
  };

  // adding telemetry log for Comments drawer open
  const handleCommentsDrawerOpen = () => {
    trackEvent('Planner_CommentsDrawerOpened', {
      scenarioId: String(scenarioId || 'new'),
      moduleId: String(moduleId || ''),
      sourceId: String(sourceId || '')
    });
    const payload = getThunkPayload();
    appDispatch(getProductsWithComments(payload));
    appDispatch(
      toggleDrawerView({
        view: DRAWER_VIEW_TYPE.COMMENTS,
        productId: null,
        productKeyRef: ''
      })
    );
  };

  // adding telemetry log for Bulk Inputs button click
  const handleBulkInputsClick = (): Promise<void> => {
    trackEvent('Planner_BulkInputsClicked', {
      scenarioId: String(scenarioId || 'new'),
      moduleId: String(moduleId || ''),
      sourceId: String(sourceId || ''),
      isDisabled: true
    });
    handleDialogVisibilityToggle();
    return Promise.resolve();
  };

  return (
    <StyledGrid id='planner-container'>
      <Grid container sx={{ padding: (theme) => theme.spacing(1, 2) }}>
        <FiltersWrapper
          accessConfig={plannerAccessConfig}
          externalReset={false}
          moduleId={moduleId}
          onValueChange={(value) => {
            performActionOnChange(value);
          }}
          onFilterFieldsFetched={onGlobalFilterFieldsFetched}
          onValueApply={handleGlobalFilterApply}
          scenarioId={scenarioId}
          setIsFilterValueChanged={() => { }}
          sourceId={sourceId}
        />
        <Grid
          xs={12}
          container
          sx={{ mt: '1px' }}
          spacing={1}
          alignItems='end'
          justifyContent='space-between'
        >
          <Grid item xs={8}>
            <Grid container spacing={1} alignItems='center'>
              <PlannerDynamicToggle />
              <Grid item>
                <Legends />
              </Grid>
            </Grid>
          </Grid>

          <Grid item md={4}>
            <Grid
              container
              spacing={1}
              justifyContent='end'
              alignItems='center'
              ml={0}
            >
              <SaveAsContainer disabled={!plannerAccessConfig?.save_as} />
              <ModuleSaveButton
                disabled={!plannerAccessConfig?.save}
                onSave={handlePlannerSave}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid
          xs={12}
          container
          sx={{ mt: '1px' }}
          spacing={1}
          justifyContent='space-between'
          alignItems='end'
        >
          <Grid item xs='auto' sx={{ minWidth: '60%' }}>
            {fetchingLocalFiltersData ? (
              FilterLoadingSkeleton(4)
            ) : (
              <LocalFilters
                formFields={localFilterFormFields}
                initialValues={localFilterInitialValues}
                isLocalFiltersReset={isLocalFiltersReset}
                onApply={(value) => handleLocalFilterApply(value)}
                onChange={(value) => {
                  appDispatch(setLocalFilterValue(value));
                }}
              />
            )}
          </Grid>
          <Grid item xs='auto'>
            <Grid
              container
              spacing={1}
              justifyContent='end'
              alignItems='flex-end'
              mt={2}
              ml={0}
            >
              <Button
                sx={{ ml: 1, mr: 1 }}
                variant='outlined'
                onClick={handleMenuOpen}
              >
                PH Tool Options
              </Button>
              <Menu
                id='basic-menu'
                anchorEl={anchorEl}
                open={isMenuOpen}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right'
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left'
                }}
                MenuListProps={{
                  'aria-labelledby': 'basic-button'
                }}
                sx={{ padding: '4px 0 4px 0' }}
              >
                <StyledMenuItem onClick={handleViewPhRecommendation}>
                  <img src='/static/images/ph-view-icon.svg' alt='' />
                  <div className='ph-menu-item'>
                    {!isViewPHRecommendations
                      ? 'View PH Recommendations'
                      : 'Hide PH Recommendations'}
                  </div>
                </StyledMenuItem>
                <StyledMenuItem onClick={handleFollowPH}>
                  <img src='/static/images/ph-follow-icon.svg' alt='' />
                  <div className='ph-menu-item'>
                    {!isFollowPH ? 'Follow PH Delist' : 'Unfollow PH Delist'}
                  </div>
                </StyledMenuItem>

                <StyledMenuItem onClick={handlePHInfo}>
                  <img src='/static/images/ph-info-icon.svg' alt='' />
                  <div className='ph-menu-item'>About PH Tool</div>
                </StyledMenuItem>
              </Menu>
              {segmentDetailsDataObject &&
                !NON_FINANCIAL_SEGMENTS.includes(
                  segmentDetailsDataObject.source_name
                    .toLowerCase()
                    .replace(/ /g, '')
                ) ? (
                <Button
                  sx={{ ml: 1 }}
                  variant='outlined'
                  disabled={!isKpiEnabled}
                  onClick={handleDialogOpen}
                >
                  Update Cogs
                </Button>
              ) : (
                ''
              )}
              {isCogsDialogOpen && (
                <CogsDialog
                  isCogsDialogOpen={isCogsDialogOpen}
                  closeCogsDialog={handleDialogClose}
                />
              )}

              <Button
                sx={{ ml: 1 }}
                variant='outlined'
                disabled
                onClick={handleBulkInputsClick}
              >
                + Bulk Inputs
              </Button>
              <Button
                sx={{ ml: 1 }}
                disabled={!plannerAccessConfig?.add_sku}
                variant='outlined'
                onClick={handleAddSKUOpen}
              >
                + Add SKU
              </Button>
              <Button
                disabled={!plannerAccessConfig?.bulk_comments}
                onClick={handleCommentsDrawerOpen}
                sx={{ ml: 1 }}
                variant='outlined'
              >
                <Grid container alignItems='center'>
                  <FaComment />
                  <Grid ml={0.5}>Comments</Grid>
                </Grid>
              </Button>
            </Grid>
          </Grid>
          <Grid item xs='auto'>
            <PHLegends />
          </Grid>
        </Grid>

        <Grid container>
          {!isFetchingFilterFields && toggledView ? (
            <>
              <Grid item xs={12} mt={1}>
                <GridTableComponentV2
                  toggle={toggledView}
                  headerRow={[
                    `Click on ${gridStaticContent} to define opportunities`,
                    ...gridLayout.column_headers
                  ]}
                  fetchingData={fetchingGridData}
                  gridData={gridLayout}
                />
              </Grid>
              <Grid
                item
                container
                xs={12}
                mt={2}
                justifyContent='space-between'
              >
                <Grid item>
                  <Typography fontSize='14px' fontWeight={700}>
                    Opportunity Summary
                  </Typography>
                </Grid>
                <Grid item>
                  <DownloadSummaryButton isDisabled={isFetchingFilterFields} />
                </Grid>
              </Grid>
              <Grid item xs={12} mt={1}>
                <OpportunitySummaryTable
                  columns={opportunitySummaryTableColumns}
                  rows={opportunitySummaryTableRows}
                />
              </Grid>
            </>
          ) : null}
        </Grid>
      </Grid>

      {isScenarioSuccessDialogOpen ? (
        <ScenarioCreateSuccessDialog
          imagePath='/static/images/success.svg'
          isOpen={isScenarioSuccessDialogOpen}
          onClose={handleScenarioCreateSuccessDialogClose}
          message='Scenario Created Successfully'
        />
      ) : null}

      {isAddSKUDialogOpen ? (
        <AddSKUDialog
          isOpen={isAddSKUDialogOpen}
          onClose={(_event, reason, refreshGridAndSummary) => {
            if (!reason) {
              handleAddSKUDialogVisibility();
            }
            if (refreshGridAndSummary) {
              const filters = transformDynamicFormValueToFilterPayload({
                combinations: globalFilterCombinations,
                formFields: globalFilterFormFields,
                formValue: globalFilterValue
              });
              getGridDataAndOpportunitySummaryTableData(
                filters,
                localFilterValue,
                localFilterFormFields,
                toggledView
              );
            }
          }}
        />
      ) : null}

      <SimulatorFooter
        isResetSimulationDisabled
        isSimulationDisabled
        nextClickHandler={handleNextFn}
        isButtonSectionVisible
      />
      {drawerViewMetadata.isDrawerOpen && (
        <DynamicDrawerBase
          open={isDrawerOpen}
          viewType={viewType}
          isFooterVisible
          onClose={onOpportunityChange}
        />
      )}
    </StyledGrid>
  );
}